import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { ItemType } from '../../../types';
import { ProjectTreeRecordType } from '../projectsTreeTypes';

type ValueType = string | number | null | undefined;

export interface ProjectItemsColumnProps {
    tree: ProjectTreeRecordType[];
    dataKey: keyof ItemType | keyof ProjectTreeRecordType;
    isSizable?: boolean;
    width?: number | string;
    onResizeEnd?: (sizes: number) => void;
    render: (value: ValueType, record: ProjectTreeRecordType) => React.ReactNode;
    flex?: number;
    minWidth?: number;
}

const SCol = styled.div<{ $flex: number | undefined; $width?: number | string }>`
    flex: ${({ $flex }) => $flex};
    min-width: 0;
    padding: 0;
    flex-direction: column;
    position: relative;
    width: ${({ $width }) => ($width ? `${$width}px` : $width)};
`;

const ResizeHandle = styled.div`
    width: 1px;
    height: 100%;
    background: rgba(197, 197, 197, 0.5);
    cursor: col-resize;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 10;
`;

let key = 0;

export const ProjectsTreeColumn: React.FC<ProjectItemsColumnProps> = ({
    dataKey,
    isSizable,
    render,
    width,
    tree,
    flex,
    minWidth,
    onResizeEnd,
}) => {
    const colRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (colRef.current && width !== undefined) {
            colRef.current.style.width = typeof width === 'number' ? `${width}px` : width;
        }
    }, [width]);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidth = colRef.current?.offsetWidth || 0;

        document.body.style.cursor = 'col-resize';

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (colRef.current) {
                const newWidth = startWidth + (moveEvent.clientX - startX);
                colRef.current.style.width = `${Math.max(newWidth, minWidth ?? 0)}px`;
            }
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'default';
            if (onResizeEnd) {
                onResizeEnd(Number(colRef.current!.style.width.replace('px', '')));
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const cells = useMemo(
        () =>
            tree.map((record) => {
                const value = (record[dataKey as keyof ProjectTreeRecordType] ??
                    record.item[dataKey as keyof ItemType]) as ValueType;
                return <React.Fragment key={`cell-${record.item.id}`}>{render(value, record)}</React.Fragment>;
            }),
        [tree, dataKey, render, minWidth],
    );

    return (
        <SCol key={`col-${dataKey}-${++key}`} ref={colRef} $width={width} $flex={flex}>
            {cells}
            {isSizable && <ResizeHandle className="project-item-size-dragger" onMouseDown={handleMouseDown} />}
        </SCol>
    );
};
